import pick from 'lodash/pick';
import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	OverrideContainerComponent,
	Store,
} from 'react-sweet-state';
import { createStore, createHook, createContainer, createActionsHook } from 'react-sweet-state';

import type { Provider } from '@atlaskit/collab-provider/provider';
import type { CollabEditProvider } from '@atlaskit/editor-common/collab';

type NativeCollabState = {
	collabEditProvider: (CollabEditProvider & Provider) | null;
	collabEditInitialized: boolean;
	hasInitialDraft: boolean;
	isDraftSyncPending: boolean;
	collabEditConnected: boolean;
};

export const initialNativeCollabState: NativeCollabState = {
	collabEditProvider: null,
	collabEditInitialized: false,
	hasInitialDraft: false,
	isDraftSyncPending: false,
	collabEditConnected: false,
};

export const actions = {
	setCollabEditProvider:
		(collabEditProvider: any) =>
		({ setState }: { setState: any }): void => {
			setState({
				collabEditProvider,
			});
		},
	setCollabEditInitialized:
		(collabEditInitialized: any) =>
		({ setState }: { setState: any }): void => {
			setState({
				collabEditInitialized,
			});
		},
	setHasInitialDraft:
		(hasInitialDraft: any) =>
		({ setState }: { setState: any }): void => {
			setState({
				hasInitialDraft,
			});
		},
	setIsDraftSyncPending:
		// PGXT-3849 Remove this if we update lodash to 5.0+ and use debounce.pending() instead

		(isDraftSyncPending: any) =>
			({ setState }: { setState: any }): void => {
				setState({
					isDraftSyncPending,
				});
			},
	setCollabEditConnected:
		(collabEditConnected: any) =>
		({ setState }: { setState: any }): void => {
			setState({
				collabEditConnected,
			});
		},
	resetNativeCollabState:
		() =>
		({ setState }: { setState: any }): void => {
			setState({
				collabEditProvider: null,
				collabEditInitialized: false,
				hasInitialDraft: false,
				isDraftSyncPending: false,
				collabEditConnected: false,
			});
		},
};

type NativeCollabStateContainerProps = {
	initialCollabState?: Partial<NativeCollabState>;
};

export const nativeCollabStore: Store<
	{
		collabEditProvider: (CollabEditProvider & Provider) | null;
		collabEditInitialized: boolean;
		hasInitialDraft: boolean;
		isDraftSyncPending: boolean;
		collabEditConnected: boolean;
	},
	{
		setCollabEditProvider: (collabEditProvider: any) => ({ setState }: { setState: any }) => void;
		setCollabEditInitialized: (
			collabEditInitialized: any,
		) => ({ setState }: { setState: any }) => void;
		setHasInitialDraft: (hasInitialDraft: any) => ({ setState }: { setState: any }) => void;
		setIsDraftSyncPending: (isDraftSyncPending: any) => ({ setState }: { setState: any }) => void;
		setCollabEditConnected: (collabEditConnected: any) => ({ setState }: { setState: any }) => void;
		resetNativeCollabState: () => ({ setState }: { setState: any }) => void;
	}
> = createStore({
	initialState: {
		...initialNativeCollabState,
	},
	actions,
	name: 'nativeCollabState',
});

export type NativeCollabActions = typeof actions;

export const NativeCollabStateContainer: OverrideContainerComponent<NativeCollabStateContainerProps> =
	createContainer<NativeCollabState, NativeCollabActions, NativeCollabStateContainerProps>(
		nativeCollabStore,
		{
			onInit:
				() =>
				async ({ dispatch }, { initialCollabState }) => {
					initialCollabState?.collabEditProvider &&
						void dispatch(actions.setCollabEditProvider(initialCollabState?.collabEditProvider));
					initialCollabState?.collabEditInitialized &&
						void dispatch(
							actions.setCollabEditInitialized(initialCollabState?.collabEditInitialized),
						);
					initialCollabState?.hasInitialDraft &&
						void dispatch(actions.setHasInitialDraft(initialCollabState?.hasInitialDraft));
					initialCollabState?.isDraftSyncPending &&
						void dispatch(actions.setIsDraftSyncPending(initialCollabState?.isDraftSyncPending));
					initialCollabState?.collabEditConnected &&
						void dispatch(actions.setCollabEditConnected(initialCollabState?.collabEditConnected));
				},
		},
	);

// The properties returned by the selector are those that most likely to change
// only at the start of the collab session.
// The properties that are changing repeatedly during the collab session could be accessed
// through `useNativeCollabDraftSync` and `useNativeCollabConnected` hooks.
// Separating the selectors in this way allows us to avoid unnecessary re-renders of the
// components that are using these hooks.
export const useNativeCollabState: HookFunction<
	Pick<
		{
			collabEditProvider: (CollabEditProvider & Provider) | null;
			collabEditInitialized: boolean;
			hasInitialDraft: boolean;
			isDraftSyncPending: boolean;
			collabEditConnected: boolean;
		},
		'collabEditProvider' | 'collabEditInitialized' | 'hasInitialDraft'
	>,
	BoundActions<
		{
			collabEditProvider: (CollabEditProvider & Provider) | null;
			collabEditInitialized: boolean;
			hasInitialDraft: boolean;
			isDraftSyncPending: boolean;
			collabEditConnected: boolean;
		},
		{
			setCollabEditProvider: (collabEditProvider: any) => ({ setState }: { setState: any }) => void;
			setCollabEditInitialized: (
				collabEditInitialized: any,
			) => ({ setState }: { setState: any }) => void;
			setHasInitialDraft: (hasInitialDraft: any) => ({ setState }: { setState: any }) => void;
			setIsDraftSyncPending: (isDraftSyncPending: any) => ({ setState }: { setState: any }) => void;
			setCollabEditConnected: (
				collabEditConnected: any,
			) => ({ setState }: { setState: any }) => void;
			resetNativeCollabState: () => ({ setState }: { setState: any }) => void;
		}
	>,
	void
> = createHook(nativeCollabStore, {
	selector: (state) =>
		pick(state, 'collabEditProvider', 'collabEditInitialized', 'hasInitialDraft'),
});

export const useNativeCollabActions: HookActionsFunction<
	BoundActions<
		{
			collabEditProvider: (CollabEditProvider & Provider) | null;
			collabEditInitialized: boolean;
			hasInitialDraft: boolean;
			isDraftSyncPending: boolean;
			collabEditConnected: boolean;
		},
		{
			setCollabEditProvider: (collabEditProvider: any) => ({ setState }: { setState: any }) => void;
			setCollabEditInitialized: (
				collabEditInitialized: any,
			) => ({ setState }: { setState: any }) => void;
			setHasInitialDraft: (hasInitialDraft: any) => ({ setState }: { setState: any }) => void;
			setIsDraftSyncPending: (isDraftSyncPending: any) => ({ setState }: { setState: any }) => void;
			setCollabEditConnected: (
				collabEditConnected: any,
			) => ({ setState }: { setState: any }) => void;
			resetNativeCollabState: () => ({ setState }: { setState: any }) => void;
		}
	>
> = createActionsHook(nativeCollabStore);

export const useNativeCollabDraftSync: HookFunction<
	Pick<
		{
			collabEditProvider: (CollabEditProvider & Provider) | null;
			collabEditInitialized: boolean;
			hasInitialDraft: boolean;
			isDraftSyncPending: boolean;
			collabEditConnected: boolean;
		},
		'isDraftSyncPending'
	>,
	BoundActions<
		{
			collabEditProvider: (CollabEditProvider & Provider) | null;
			collabEditInitialized: boolean;
			hasInitialDraft: boolean;
			isDraftSyncPending: boolean;
			collabEditConnected: boolean;
		},
		{
			setCollabEditProvider: (collabEditProvider: any) => ({ setState }: { setState: any }) => void;
			setCollabEditInitialized: (
				collabEditInitialized: any,
			) => ({ setState }: { setState: any }) => void;
			setHasInitialDraft: (hasInitialDraft: any) => ({ setState }: { setState: any }) => void;
			setIsDraftSyncPending: (isDraftSyncPending: any) => ({ setState }: { setState: any }) => void;
			setCollabEditConnected: (
				collabEditConnected: any,
			) => ({ setState }: { setState: any }) => void;
			resetNativeCollabState: () => ({ setState }: { setState: any }) => void;
		}
	>,
	void
> = createHook(nativeCollabStore, {
	selector: (state) => pick(state, 'isDraftSyncPending'),
});

export const useNativeCollabConnected: HookFunction<
	Pick<
		{
			collabEditProvider: (CollabEditProvider & Provider) | null;
			collabEditInitialized: boolean;
			hasInitialDraft: boolean;
			isDraftSyncPending: boolean;
			collabEditConnected: boolean;
		},
		'collabEditConnected'
	>,
	BoundActions<
		{
			collabEditProvider: (CollabEditProvider & Provider) | null;
			collabEditInitialized: boolean;
			hasInitialDraft: boolean;
			isDraftSyncPending: boolean;
			collabEditConnected: boolean;
		},
		{
			setCollabEditProvider: (collabEditProvider: any) => ({ setState }: { setState: any }) => void;
			setCollabEditInitialized: (
				collabEditInitialized: any,
			) => ({ setState }: { setState: any }) => void;
			setHasInitialDraft: (hasInitialDraft: any) => ({ setState }: { setState: any }) => void;
			setIsDraftSyncPending: (isDraftSyncPending: any) => ({ setState }: { setState: any }) => void;
			setCollabEditConnected: (
				collabEditConnected: any,
			) => ({ setState }: { setState: any }) => void;
			resetNativeCollabState: () => ({ setState }: { setState: any }) => void;
		}
	>,
	void
> = createHook(nativeCollabStore, {
	selector: (state) => pick(state, 'collabEditConnected'),
});
